import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { ChevronDown, ChevronUp, Clock, Users, Radio, Play, ExternalLink, X, CheckCircle2, Eye, Headphones, Download } from "lucide-react";
import BookmarkButton from "./BookmarkButton";
import AskQuestionDialog from "./AskQuestionDialog";

/**
 * Track-grouped schedule view.
 *
 * Pulls /api/conference/schedule and renders each day with sessions grouped
 * by `track`. Tracks are ordered by their first session's time. Breaks are
 * shown in a slim utility row so the headline tracks don't get drowned out.
 *
 * Variants:
 *   variant="dark"  → dark-on-light card style (Landing public schedule)
 *   variant="light" → compact panel style (Portal)
 */
export default function TrackGroupedSchedule({
  variant = "dark",
  days = null,
  showAsk = false,
  rooms = [],
  onWatchRoom = null,
  activeRoomId = null,
  searchQuery = "",
  watchProgress = {},
  onMarkWatched = null,
  isBookmarked = null,
  onToggleBookmark = null,
  externalPlayingTalk = null,
  onTalkModalChange = null,
}) {
  const [schedule, setSchedule] = useState([]);
  const [trackQA, setTrackQA] = useState({}); // {`${day_index}|${track}`: {video, video_embed, audio}}
  const [internalPlayingTalk, setInternalPlayingTalk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null);

  // Allow parent (Portal) to open the modal for a talk picked from the
  // discovery rails / playlist without duplicating the modal markup.
  const playingTalk = externalPlayingTalk ?? internalPlayingTalk;
  const setPlayingTalk = (t) => {
    if (onTalkModalChange) onTalkModalChange(t);
    else setInternalPlayingTalk(t);
  };

  useEffect(() => {
    api.get("/conference/schedule")
      .then((r) => {
        const s = r.data?.schedule || [];
        setSchedule(s);
        if (s.length) setActiveDay(s[0].day_index);
      })
      .catch(() => setSchedule([]))
      .finally(() => setLoading(false));
    api.get("/conference/track-qa")
      .then((r) => {
        const map = {};
        for (const q of (r.data?.items || [])) {
          map[`${q.day_index}|${q.track}`] = {
            video_url: q.video_url,
            video_embed_url: q.video_embed_url,
            audio_url: q.audio_url,
          };
        }
        setTrackQA(map);
      })
      .catch(() => setTrackQA({}));
  }, []);

  const filteredDays = useMemo(() => {
    const trimmed = (searchQuery || "").trim().toLowerCase();
    const base = !days || days <= 0 ? schedule : schedule.slice(0, days);
    if (!trimmed) return base;
    return base
      .map((day) => {
        const matches = (day.sessions || []).filter((s) => {
          const hay = [
            s.title || "",
            s.speaker || "",
            s.track || "",
            s.room_name || "",
            s.description || "",
          ].join(" \u0001 ").toLowerCase();
          return hay.includes(trimmed);
        });
        return { ...day, sessions: matches };
      })
      .filter((day) => (day.sessions || []).length > 0);
  }, [schedule, days, searchQuery]);

  if (loading) {
    return (
      <div className="rounded-3xl bg-white/60 ring-1 ring-black/5 p-12 text-center text-slate-400 text-sm">
        Loading schedule…
      </div>
    );
  }

  if (!filteredDays.length) {
    const trimmed = (searchQuery || "").trim();
    return (
      <div className="rounded-3xl bg-white/60 ring-1 ring-black/5 p-12 text-center text-slate-400 text-sm">
        {trimmed
          ? <>No talks match <span className="font-medium text-slate-600">&ldquo;{trimmed}&rdquo;</span>. Try a different speaker or topic.</>
          : "Programme will be published soon."}
      </div>
    );
  }

  const activeDayObj = filteredDays.find((d) => d.day_index === activeDay) || filteredDays[0];

  return (
    <div className="space-y-6" data-testid="track-grouped-schedule">
      {/* Day tab strip */}
      <div className="flex flex-wrap gap-2" data-testid="schedule-day-tabs">
        {filteredDays.map((d) => {
          const isActive = d.day_index === activeDayObj.day_index;
          return (
            <button
              key={d.day_index}
              type="button"
              onClick={() => setActiveDay(d.day_index)}
              data-testid={`schedule-day-tab-${d.day_index}`}
              className={
                "px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold tracking-tight transition-colors ring-1 " +
                (isActive
                  ? "bg-[#0F172A] text-white ring-[#0F172A]"
                  : "bg-white text-slate-700 ring-black/10 hover:bg-slate-50")
              }
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-70 mr-2">D{d.day_index}</span>
              {prettyDay(d.day)}
            </button>
          );
        })}
      </div>

      <DayView
        key={activeDayObj.day_index}
        day={activeDayObj}
        variant={variant}
        showAsk={showAsk}
        rooms={rooms}
        onWatchRoom={onWatchRoom}
        activeRoomId={activeRoomId}
        onPlayTalk={(s) => {
          setPlayingTalk(s);
          if (onMarkWatched) onMarkWatched(s.session_id, "in_progress");
        }}
        watchProgress={watchProgress}
        isBookmarked={isBookmarked}
        onToggleBookmark={onToggleBookmark}
        trackQA={trackQA}
      />

      {playingTalk && (
        <TalkModal
          session={playingTalk}
          watchStatus={watchProgress?.[playingTalk.session_id]?.status}
          onMarkWatched={onMarkWatched ? () => onMarkWatched(playingTalk.session_id, "watched") : null}
          bookmarked={isBookmarked ? isBookmarked(playingTalk.session_id) : false}
          onToggleBookmark={onToggleBookmark ? () => onToggleBookmark(playingTalk) : null}
          onClose={() => setPlayingTalk(null)}
        />
      )}
    </div>
  );
}

function TalkModal({ session, watchStatus, onMarkWatched, bookmarked, onToggleBookmark, onClose }) {
  // Decide initial mode: if caller passed `_open_mode: "audio"` (from the
  // Listen button) prefer audio; else if no video, audio; else video.
  const initialMode = session._open_mode === "audio"
    ? "audio"
    : (session.video_embed_url || session.video_url ? "video" : "audio");
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const videoSrc = session.video_embed_url || session.video_url;
  const audioSrc = session.audio_url;
  const hasVideo = !!videoSrc;
  const hasAudio = !!audioSrc;
  const isWatched = watchStatus === "watched";
  const safeFilename = (session.title || "talk").replace(/[^\w\d]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="talk-modal"
    >
      <div
        className="w-full max-w-5xl bg-[#0F172A] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">{session.track || "Talk"}</div>
            <div className="font-medium text-white truncate">{session.title}</div>
            {session.speaker && <div className="text-xs text-white/60 truncate">{session.speaker}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasVideo && hasAudio && (
              <div className="hidden sm:flex items-center bg-white/5 rounded-full p-0.5" role="tablist">
                <button
                  type="button"
                  onClick={() => setMode("video")}
                  className={
                    "px-3 h-8 rounded-full text-[11px] font-semibold transition-colors flex items-center gap-1.5 " +
                    (mode === "video" ? "bg-white text-slate-900" : "text-white/70 hover:text-white")
                  }
                  data-testid="talk-modal-tab-watch"
                  role="tab"
                  aria-selected={mode === "video"}
                >
                  <Play size={11} strokeWidth={2.4} /> Watch
                </button>
                <button
                  type="button"
                  onClick={() => setMode("audio")}
                  className={
                    "px-3 h-8 rounded-full text-[11px] font-semibold transition-colors flex items-center gap-1.5 " +
                    (mode === "audio" ? "bg-white text-slate-900" : "text-white/70 hover:text-white")
                  }
                  data-testid="talk-modal-tab-listen"
                  role="tab"
                  aria-selected={mode === "audio"}
                >
                  <Headphones size={11} strokeWidth={2.4} /> Listen
                </button>
              </div>
            )}
            {onToggleBookmark && (
              <BookmarkButton
                sessionId={session.session_id}
                bookmarked={bookmarked}
                onToggle={onToggleBookmark}
                variant="button"
              />
            )}
            {onMarkWatched && (
              <button
                type="button"
                onClick={() => onMarkWatched()}
                disabled={isWatched}
                className={
                  "hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-semibold transition-colors " +
                  (isWatched
                    ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30 cursor-default"
                    : "bg-white/10 text-white hover:bg-white/20")
                }
                data-testid="talk-modal-mark-watched"
                title={isWatched ? "Marked as watched" : "Mark as watched"}
              >
                {isWatched ? <CheckCircle2 size={14} strokeWidth={2.2} /> : <Eye size={14} strokeWidth={2.2} />}
                {isWatched ? "Watched" : "Mark as watched"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center text-white"
              data-testid="talk-modal-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        {mode === "video" && hasVideo ? (
          <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={videoSrc}
              title={session.title}
              className="absolute inset-0 w-full h-full border-0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : mode === "audio" && hasAudio ? (
          <div className="bg-gradient-to-br from-violet-900 via-violet-800 to-slate-900 px-6 sm:px-10 py-10 sm:py-14" data-testid="talk-modal-audio-player">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 text-violet-200 text-[10px] uppercase tracking-[0.22em] font-semibold">
                <div className="h-9 w-9 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                  <Headphones size={14} strokeWidth={2.2} />
                </div>
                Podcast version · audio only
              </div>
              <h3 className="mt-4 font-heading text-2xl sm:text-3xl text-white leading-tight">{session.title}</h3>
              {session.speaker && <p className="mt-2 text-violet-100/80">{session.speaker}</p>}
              <audio
                key={audioSrc}
                src={audioSrc}
                controls
                preload="metadata"
                className="mt-7 w-full rounded-xl"
                data-testid="talk-modal-audio"
              />
              <div className="mt-5 flex items-center gap-3 flex-wrap text-xs">
                <a
                  href={audioSrc}
                  download={`${safeFilename}.mp3`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors font-semibold"
                  data-testid="talk-modal-audio-download"
                >
                  <Download size={12} strokeWidth={2.4} /> Download MP3
                </a>
                <span className="text-violet-200/60 text-[11px]">
                  Tip: long-press the download link on mobile to save to Files.
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-900 px-6 py-12 text-center text-white/60 text-sm">
            No video or audio available for this talk yet.
          </div>
        )}
      </div>
    </div>
  );
}

function DayView({ day, variant, showAsk, rooms, onWatchRoom, activeRoomId, onPlayTalk, watchProgress, isBookmarked, onToggleBookmark, trackQA }) {
  const { tracks, breaks } = useMemo(() => groupByTrack(day.sessions || []), [day]);
  // Sorted list of distinct slot start times across the whole day. Used to
  // compute the end of each track-block (block end = next slot start).
  const slotEndByStart = useMemo(() => {
    const times = [...new Set((day.sessions || []).map((s) => s.time).filter(Boolean))].sort();
    const m = new Map();
    times.forEach((t, i) => m.set(t, times[i + 1] || null));
    return m;
  }, [day]);

  return (
    <div className="space-y-4">
      {tracks.length === 0 && breaks.length === 0 && (
        <div className="text-sm text-slate-500 px-1">No sessions for this day yet.</div>
      )}

      {tracks.map((t) => (
        <TrackCard
          key={t.name}
          track={t}
          variant={variant}
          showAsk={showAsk}
          rooms={rooms}
          onWatchRoom={onWatchRoom}
          activeRoomId={activeRoomId}
          blockEndTime={slotEndByStart.get(t.firstTime) || null}
          onPlayTalk={onPlayTalk}
          watchProgress={watchProgress}
          isBookmarked={isBookmarked}
          onToggleBookmark={onToggleBookmark}
          qa={trackQA ? trackQA[`${day.day_index}|${t.name}`] : null}
        />
      ))}

      {breaks.length > 0 && (
        <div className="rounded-2xl bg-slate-50 ring-1 ring-black/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2 flex items-center gap-2">
            <Clock size={11} /> Breaks
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-600">
            {breaks.map((b) => (
              <div key={b.session_id} className="flex items-center gap-2">
                <span className="font-mono text-slate-400">{b.time}</span>
                <span>{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrackCard({ track, variant, showAsk, rooms, onWatchRoom, activeRoomId, blockEndTime, onPlayTalk, watchProgress, isBookmarked, onToggleBookmark, qa }) {
  const [open, setOpen] = useState(true);
  const [qaModal, setQaModal] = useState(null); // "video" | "audio" | null
  const isDark = variant === "dark";
  // All sessions in a single track broadcast in the same room. Use the first one.
  const trackRoomId = track.sessions[0]?.room_id || null;
  const trackRoom = rooms.find((r) => r.id === trackRoomId);
  const timeRange = blockEndTime ? `${track.firstTime} – ${blockEndTime}` : track.firstTime;
  const kindLabel = trackKind(track.name); // e.g. "Keynote 1", "Concurrent 1"
  // The `track` field is the authoritative grouping. When the track explicitly
  // names a room (Plenary / Concurrent N / Keynote N / Workshop), trust the
  // track for the badge — this means admins only have to edit one field when
  // a talk gets reassigned between rooms.
  const trackImpliesRoom = /^(plenary|keynote|concurrent\s*\d+|workshop|bubble\s*wrap)/i.test(track.name);
  const badgeLabel = trackImpliesRoom ? kindLabel : (trackRoom?.name || kindLabel);
  const headerColor = trackImpliesRoom ? trackColor(track.name) : roomColor(trackRoomId);

  return (
    <div
      className={
        "rounded-3xl ring-1 ring-black/5 overflow-hidden bg-white"
      }
      data-testid={`track-card-${slug(track.name)}`}
    >
      <div className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-4 min-w-0 text-left flex-1 rounded-xl hover:opacity-90 transition-opacity"
          data-testid={`track-toggle-${slug(track.name)}`}
        >
          <span
            className="shrink-0 inline-flex items-center justify-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold"
            style={{ backgroundColor: headerColor.bg, color: headerColor.fg }}
          >
            {badgeLabel}
          </span>
          <div className="min-w-0">
            <div className={"font-heading text-lg sm:text-xl font-medium tracking-tight truncate " + (isDark ? "text-slate-900" : "text-slate-900")}>
              {trackTitle(track.name)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span className="text-slate-600 font-medium">{kindLabel}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={11} /> {timeRange}</span>
              <span className="inline-flex items-center gap-1.5"><Users size={11} /> {track.sessions.length} talk{track.sessions.length === 1 ? "" : "s"}</span>
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {trackRoom && onWatchRoom && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onWatchRoom(trackRoom.id); }}
              className={
                "hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-colors " +
                (activeRoomId === trackRoom.id
                  ? "bg-[#0F172A] text-white hover:bg-slate-800"
                  : "bg-[#28BBE3]/10 text-[#0F8FB0] hover:bg-[#28BBE3]/20 ring-1 ring-[#28BBE3]/30")
              }
              data-testid={`track-watch-${slug(track.name)}`}
              title={`Watch in ${trackRoom.name}`}
            >
              <Play size={12} strokeWidth={2.4} />
              {activeRoomId === trackRoom.id ? "Watching" : "Watch live"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="p-1.5 rounded-lg hover:bg-slate-100"
            aria-label={open ? "Collapse" : "Expand"}
          >
            {open ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="divide-y divide-black/5 border-t border-black/5">
          {track.sessions.map((s, idx) => (
            <div
              key={s.session_id}
              className="grid grid-cols-12 gap-3 sm:gap-4 px-6 sm:px-8 py-4 hover:bg-slate-50/60 transition-colors items-start"
              data-testid={`session-row-${s.session_id}`}
            >
              <div className="col-span-12 sm:col-span-2 font-mono text-xs sm:text-sm text-slate-400 pt-0.5">
                Talk {idx + 1}
              </div>
              <div className="col-span-12 sm:col-span-7">
                <div className="text-slate-900 font-medium leading-snug flex items-start gap-2 flex-wrap">
                  <span>{s.title || "TBC"}</span>
                  {watchProgress?.[s.session_id]?.status === "watched" && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 mt-0.5"
                      data-testid={`watched-badge-${s.session_id}`}
                    >
                      <CheckCircle2 size={10} strokeWidth={2.4} /> Watched
                    </span>
                  )}
                  {watchProgress?.[s.session_id]?.status === "in_progress" && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 mt-0.5"
                      data-testid={`inprogress-badge-${s.session_id}`}
                    >
                      <Eye size={10} strokeWidth={2.4} /> In progress
                    </span>
                  )}
                </div>
                {s.speaker && (
                  <div className="text-sm text-slate-500 mt-1">{s.speaker}</div>
                )}
                {s.description && (
                  <div className="text-xs text-slate-500 mt-1.5 italic">{s.description}</div>
                )}
                {(s.links || []).length > 0 && (
                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {s.links.map((l, i) => (
                      <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#28BBE3]/10 text-[#0F8FB0] hover:bg-[#28BBE3]/20 transition-colors ring-1 ring-[#28BBE3]/30"
                        data-testid={`session-link-${s.session_id}-${i}`}
                      >
                        <ExternalLink size={10} strokeWidth={2.4} />
                        {l.label || "Open link"}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-12 sm:col-span-3 text-left sm:text-right flex flex-col sm:items-end gap-1.5">
                <div className="flex items-center gap-1.5 sm:justify-end">
                  {onToggleBookmark && isBookmarked && (
                    <BookmarkButton
                      sessionId={s.session_id}
                      bookmarked={isBookmarked(s.session_id)}
                      onToggle={() => onToggleBookmark(s)}
                      variant="icon"
                    />
                  )}
                  {s.video_url && onPlayTalk && (
                    <button
                      type="button"
                      onClick={() => onPlayTalk(s)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-[#28BBE3] text-white hover:bg-[#1FA8CC] transition-colors shadow-[0_6px_20px_-8px_rgba(40,187,227,0.6)]"
                      data-testid={`watch-talk-${s.session_id}`}
                    >
                      <Play size={11} strokeWidth={2.6} />
                      Watch talk
                    </button>
                  )}
                  {!s.video_url && s.audio_url && onPlayTalk && (
                    <button
                      type="button"
                      onClick={() => onPlayTalk({ ...s, _open_mode: "audio" })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-[0_6px_20px_-8px_rgba(124,58,237,0.5)]"
                      data-testid={`listen-talk-${s.session_id}`}
                    >
                      <Headphones size={11} strokeWidth={2.6} />
                      Listen
                    </button>
                  )}
                  {s.video_url && s.audio_url && onPlayTalk && (
                    <button
                      type="button"
                      onClick={() => onPlayTalk({ ...s, _open_mode: "audio" })}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-100 text-slate-600 hover:bg-violet-100 hover:text-violet-700 transition-colors"
                      data-testid={`listen-talk-${s.session_id}`}
                      title="Listen (podcast version)"
                      aria-label="Listen as podcast"
                    >
                      <Headphones size={12} strokeWidth={2.4} />
                    </button>
                  )}
                </div>
                {showAsk && (
                  <AskQuestionDialog
                    sessionId={s.session_id}
                    sessionTitle={s.title}
                    sessionSpeaker={s.speaker}
                    testIdPrefix={`ask-${s.session_id}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && qa && (qa.video_url || qa.audio_url) && (
        <div
          className="border-t border-black/5 bg-gradient-to-r from-slate-50 to-white px-6 sm:px-8 py-4 flex items-center gap-3 flex-wrap"
          data-testid={`track-qa-footer-${slug(track.name)}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#28BBE3]" /> Q+A recording
          </div>
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            {qa.video_url && (
              <button
                type="button"
                onClick={() => setQaModal("video")}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold bg-[#28BBE3] text-white hover:bg-[#1FA8CC] transition-colors shadow-[0_6px_20px_-8px_rgba(40,187,227,0.6)]"
                data-testid={`watch-qa-${slug(track.name)}`}
              >
                <Play size={11} strokeWidth={2.6} />
                Watch Q+A
              </button>
            )}
            {qa.audio_url && (
              <button
                type="button"
                onClick={() => setQaModal("audio")}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-bold bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-[0_6px_20px_-8px_rgba(124,58,237,0.5)]"
                data-testid={`listen-qa-${slug(track.name)}`}
              >
                <Headphones size={11} strokeWidth={2.6} />
                Listen Q+A
              </button>
            )}
          </div>
        </div>
      )}

      {qaModal && qa && (
        <QAModal
          trackName={track.name}
          videoSrc={qaModal === "video" ? (qa.video_embed_url || qa.video_url) : null}
          audioSrc={qaModal === "audio" ? qa.audio_url : null}
          onClose={() => setQaModal(null)}
        />
      )}
    </div>
  );
}

function QAModal({ trackName, videoSrc, audioSrc, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const safeFilename = (trackName || "qa").replace(/[^\w\d]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="qa-modal"
    >
      <div
        className="w-full max-w-5xl bg-[#0F172A] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">Q+A recording</div>
            <div className="font-medium text-white truncate">{trackName}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center text-white shrink-0"
            data-testid="qa-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {videoSrc ? (
          <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={videoSrc}
              title={`Q+A · ${trackName}`}
              className="absolute inset-0 w-full h-full border-0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : audioSrc ? (
          <div className="bg-gradient-to-br from-violet-900 via-violet-800 to-slate-900 px-6 sm:px-10 py-10 sm:py-14" data-testid="qa-modal-audio-player">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 text-violet-200 text-[10px] uppercase tracking-[0.22em] font-semibold">
                <div className="h-9 w-9 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                  <Headphones size={14} strokeWidth={2.2} />
                </div>
                Podcast version · audio only
              </div>
              <h3 className="mt-4 font-heading text-2xl sm:text-3xl text-white leading-tight">Q+A · {trackName}</h3>
              <audio
                key={audioSrc}
                src={audioSrc}
                controls
                preload="metadata"
                className="mt-7 w-full rounded-xl"
                data-testid="qa-modal-audio"
              />
              <div className="mt-5 flex items-center gap-3 flex-wrap text-xs">
                <a
                  href={audioSrc}
                  download={`${safeFilename}-qa.mp3`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors font-semibold"
                  data-testid="qa-modal-audio-download"
                >
                  <Download size={12} strokeWidth={2.4} /> Download MP3
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* helpers                                                                    */
/* -------------------------------------------------------------------------- */

function groupByTrack(sessions) {
  const groups = new Map();
  const breaks = [];
  const BREAK_TITLE_RX = /^(morning tea|afternoon tea|lunch|tea break|coffee break|registration|exhibition)/i;
  for (const s of sessions) {
    const track = (s.track || "").trim();
    const looksLikeBreak =
      !track ||
      /^break$/i.test(track) ||
      /^exhibition/i.test(track) ||
      BREAK_TITLE_RX.test(s.title || "");
    if (looksLikeBreak) {
      breaks.push(s);
      continue;
    }
    if (!groups.has(track)) groups.set(track, []);
    groups.get(track).push(s);
  }
  // Sort each group's sessions by time, then explicit sort_order, then title.
  for (const [, arr] of groups) {
    arr.sort((a, b) =>
      (a.time || "").localeCompare(b.time || "")
      || ((a.sort_order ?? 99) - (b.sort_order ?? 99))
      || (a.title || "").localeCompare(b.title || "")
    );
  }
  // Sort tracks by first session time, then by name.
  const tracks = [...groups.entries()].map(([name, arr]) => ({
    name,
    sessions: arr,
    firstTime: arr[0]?.time || "",
  })).sort((a, b) => (a.firstTime).localeCompare(b.firstTime) || a.name.localeCompare(b.name));
  breaks.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  return { tracks, breaks };
}

function trackKind(name) {
  const lower = name.toLowerCase();
  if (lower.includes("keynote")) {
    const m = name.match(/keynote\s*(\d+)/i);
    return m ? `Keynote ${m[1]}` : "Keynote";
  }
  if (lower.includes("concurrent")) {
    const m = name.match(/concurrent\s*(\d+)/i);
    return m ? `Concurrent ${m[1]}` : "Concurrent";
  }
  if (lower.includes("bubble wrap")) return "Concurrent 1";
  if (lower.includes("plenary")) return "Plenary";
  if (lower.includes("workshop")) return "Workshop";
  return "Session";
}

function trackTitle(name) {
  // Strip leading "Keynote 1: " / "Concurrent 1: " / etc.
  return name.replace(/^(keynote|concurrent|plenary)\s*\d*\s*:\s*/i, "").trim() || name;
}

function trackColor(name) {
  const lower = name.toLowerCase();
  if (lower.includes("keynote")) return { bg: "#0F172A", fg: "#FFFFFF" };
  if (lower.includes("concurrent 1")) return { bg: "#28BBE3", fg: "#0F172A" };
  if (lower.includes("concurrent 2")) return { bg: "#DC4F48", fg: "#FFFFFF" };
  if (lower.includes("concurrent 3")) return { bg: "#F5B400", fg: "#0F172A" };
  if (lower.includes("bubble wrap") || lower.includes("plenary")) return { bg: "#4A90E2", fg: "#FFFFFF" };
  return { bg: "#E2E8F0", fg: "#0F172A" };
}

// Color per Vimeo room (single source of truth for badges).
function roomColor(roomId) {
  if (roomId === "concurrent-2") return { bg: "#DC4F48", fg: "#FFFFFF" };
  if (roomId === "concurrent-3") return { bg: "#F5B400", fg: "#0F172A" };
  // plenary or unknown → main stage dark
  return { bg: "#0F172A", fg: "#FFFFFF" };
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function prettyDay(label) {
  // "Day 1 - Monday 22 June 2026" → "Mon 22 Jun"
  if (!label) return "";
  const m = label.match(/-\s*(\w+)\s+(\d+)\s+(\w+)/i);
  if (m) {
    const wk = m[1].slice(0, 3);
    const day = m[2];
    const month = m[3].slice(0, 3);
    return `${wk} ${day} ${month}`;
  }
  return label;
}
